import * as Yup from 'yup'

const requiredString = (label: string) =>
    Yup.string()
        .trim()
        .required()
        .label(label)

const requiredNumber = (label: string) =>
    Yup.number()
        .required()
        .label(label)


// const cbaRequiredNumber = (label: string) =>
//     Yup.number().when("benefit_monetisation_mode", {
//         is: "cba",
//         then: (schema) =>
//             schema
//                 .typeError(`${label} must be a number`)
//                 .required(`${label} is required`)
//                 .moreThan(0, `${label} must be greater than 0`),
//         otherwise: (schema) => schema.nullable(),
//     });

// const ceaRequiredNumber = (label: string) =>
//     Yup.number().when("benefit_monetisation_mode", {
//         is: "cea",
//         then: (schema) =>
//             schema
//                 .typeError(`${label} must be a number`)
//                 .required(`${label} is required`)
//                 .moreThan(0, `${label} must be greater than 0`),
//         otherwise: (schema) => schema.nullable(),
//     });


export const strategicResourcingStageSchema = {
    options_library_engine: Yup.object({
        justification_of_option: Yup.object()
            .test(
            "min-3-justifications",
            "At least 3 justifications are required",
            (value) => {
                if (!value) {
                  return false;
                }
                return Object.values(value).length >= 3;
            }
        ),
    }),

    economies_of_scale_engine: Yup.object({
        economies_of_scale_assessment: requiredString('Economies of Scale Assessment'),
    }),

    special_procurement_actions_and_social_impact: Yup.object({
        gender_responsive_procurement: requiredString('Gender-responsive Procurement'),
        social_benefits_value_reference: requiredString('Social Benefits & Value (Reference)'),
        sustainability_and_environmental_compliance: requiredString('Sustainability and Environmental Compliance'),
        legal_and_technical_due_diligence: requiredString('Legal and Technical Due Diligence'),
    }),

    fiscal_sustainability_engine_ifsa: Yup.object({
        operating_cost_coverage: requiredNumber('Operating Cost Coverage').min(0, 'Operating Cost Coverage must be between 0 and 100').max(100, 'Operating Cost Coverage must be between 0 and 100'),
        lifecycle_cost_coverage: requiredNumber('Lifecycle Cost Coverage').min(0, 'Lifecycle Cost Coverage must be between 0 and 100').max(100, 'Lifecycle Cost Coverage must be between 0 and 100'),
        revenue_sufficiency: requiredNumber('Revenue Sufficiency').min(0, 'Revenue Sufficiency must be between 0 and 100').max(100, 'Revenue Sufficiency must be between 0 and 100'),
        fiscal_affordability_index: requiredNumber('Fiscal Affordability Index').min(0, 'Fiscal Affordability Index must be between 0 and 100').max(100, 'Fiscal Affordability Index must be between 0 and 100'),
        funding_gap_percentage: requiredNumber('Funding Gap').min(0, 'Funding Gap must be between 0 and 100').max(100, 'Funding Gap must be between 0 and 100'),
        debt_exposure: requiredNumber('Debt Exposure'),
        contingent_liability_exposure: requiredString('Contingent Liability Exposure')
    }),

    risk_and_sensitivity_analysis_engine: Yup.object({
        capex_scenario: requiredNumber('Capex Scenario'),
        demand_scenario: requiredNumber('Demand Scenario'),
        inflation_scenario: requiredNumber('Inflation Scenario').min(0, 'Inflation Scenario must be between 0 and 100').max(100, 'Inflation Scenario must be between 0 and 100'),
        revenue_scenario: requiredNumber('Revenue Scenario'),
        schedule_delay_months: requiredNumber('Schedule Delay Months')
    }),

    demand_and_needs_validation_engine: Yup.object({
        current_demand_units: requiredNumber('Current Demand').min(1, "Current Demand must be greater than 0"),
        future_demand_horizon_year: requiredNumber('Future Demand').min(1, "Future Demand must be greater than 0"),
        population_served: requiredNumber('Population Served').min(1, "Population Served must be greater than 0"),
        growth_rate: requiredNumber('Growth Rate').min(0, 'Growth Rate must be between 0 and 100').max(100, 'Growth Rate must be between 0 and 100'),
        peak_capacity_required: requiredNumber('Peak Capacity Required').min(1, "Peak Capacity Required must be greater than 0"),
        demand_confidence_level: requiredString('Demand Confidence Level'),
    }),
    base_case_and_counterfactual_scenario_engine: Yup.object({
        counterfactual_scenario_type: requiredString('Counterfactual Scenario Type'),
        current_capacity: requiredString('Current Capacity'),
        current_demand: requiredString('Current Demand'),
        current_service_gap: Yup.number()
            .typeError('Current Service Gap % must be a number')
            .min(0, 'Cannot be less than 0')
            .max(100, 'Cannot exceed 100')
            .required()
            .label('Current Service Gap %'),
        current_cost_of_service: requiredString('Current Cost of Service'),
        consequences_of_no_intervention: Yup.array().of(Yup.string()),
        base_case_confidence_level: requiredString('Base Case Confidence Level'),
    }),
    financial_and_funding_strategy_engine: Yup.object({
        funding_sources: Yup.array().of(Yup.string()),
        funding_secured: Yup.number()
            .transform((value, original) => (original === '' ? undefined : value))
            .typeError('Funding Secured must be a number')
            .notRequired(),
        funding_pending: Yup.number()
            .transform((value, original) => (original === '' ? undefined : value))
            .typeError('Funding Pending must be a number')
            .notRequired(),
        funding_gap: Yup.number()
            .transform((value, original) => (original === '' ? undefined : value))
            .typeError('Funding Gap must be a number')
            .notRequired(),
        private_sector_contribution: Yup.number()
            .transform((value, original) => (original === '' ? undefined : value))
            .typeError('Private Sector Contribution must be a number')
            .min(0, 'Cannot be less than 0')
            .max(100, 'Cannot exceed 100')
            .notRequired(),
        public_sector_contribution: Yup.number()
            .transform((value, original) => (original === '' ? undefined : value))
            .typeError('Public Sector Contribution must be a number')
            .min(0, 'Cannot be less than 0')
            .max(100, 'Cannot exceed 100')
            .notRequired(),
        revenue_model: Yup.string(),
        innovative_financing_mechanisms: Yup.array().of(Yup.string()),
        innovative_financing_structure_and_application: Yup.string(),
    }),

    cba_cea_intelligence_engine: Yup.object({
        capital_cost: requiredNumber('Capital Cost'),
        annual_operating_cost: requiredNumber('Annual Operating Cost'),
        economic_benefits: requiredNumber('Economic Benefits'),
        social_benefits: requiredNumber('Social Benefits'),
        residual_value: requiredNumber('Residual Value'),
        discount_rate: requiredNumber('Discount Rate').min(0, 'Discount Rate must be between 0 and 100').max(100, 'Discount Rate must be between 0 and 100'),
        analysis_period_years: requiredNumber('Analysis Period Years'),
    })
}

export type IStrategicResourcingStageSchema =
    keyof typeof strategicResourcingStageSchema